import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  IconButton,
  Box,
} from '@mui/material';

import { Warning, Delete, Close } from '@mui/icons-material';
import Button from './Button';

const DeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title = 'Delete Confirmation',
  message = 'Are you sure you want to delete this item? This action cannot be undone.',
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isLoading = false,
  loadingText = 'Deleting...',
  severity = 'error',
}) => {
  const { t } = useTranslation();
  const getSeverityColor = () => {
    switch (severity) {
      case 'warning':
        return '#ff9800';
      case 'info':
        return '#2196f3';
      default:
        return '#f44336';
    }
  };

  const getSeverityIcon = () => {
    switch (severity) {
      case 'warning':
        return <Warning style={{ color: '#ff9800' }} />;
      case 'info':
        return <Warning style={{ color: '#2196f3' }} />;
      default:
        return <Warning style={{ color: '#f44336' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '12px',
          padding: '8px',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {getSeverityIcon()}
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
        <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: '16px 24px' }}>
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ padding: '16px 24px', gap: 2 }}>
        <Button text={t('cancel')} onClick={onClose} disabled={isLoading} bg="light" />
        <Button
          text={isLoading ? loadingText : t('delete')}
          onClick={onConfirm}
          disabled={isLoading}
          loading={isLoading}
          startIcon={!isLoading ? <Delete /> : null}
          style={{
            backgroundColor: severity === 'error' ? '#f44336' : severity === 'warning' ? '#ff9800' : '#26786a',
            '&:hover': {
              backgroundColor: severity === 'error' ? '#d32f2f' : severity === 'warning' ? '#f57c00' : '#339489',
            },
          }}
        />
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
